import { useState } from "react";
import { useParams } from "react-router-dom";
import { getTataAgents, callMergeApi, getAllCallLogsByTicketId } from "../../http";
import { ShowAlert } from "../../utils/showAlert";
import { UseGetStoreData } from "../getStoreData/UseGetStoreData";

interface Agent {
  id: string;
  name: string;
}

export const useCallMerge = () => {
  const { id: paramId } = useParams();
  const { getTicketData } = UseGetStoreData();
  const id = paramId || getTicketData?._id;
  const [isOpen, setIsOpen] = useState(false);
  const [agents, setAgents] = useState<Agent[]>([]);
  const [loading, setLoading] = useState(false);
  const [merging, setMerging] = useState<string | null>(null);
  const [monitorUCID, setMonitorUCID] = useState<string | null>(null);

  const fetchAgentsAndCallId = async () => {
    if (!id) return;
    setLoading(true);
    try {
      const [agentsRes, callLogRes] = await Promise.all([
        getTataAgents(),
        getAllCallLogsByTicketId(id),
      ]);
      const agentsData = agentsRes.data;
      const agentsList = Array.isArray(agentsData) ? agentsData : Array.isArray(agentsData?.data) ? agentsData.data : [];
      setAgents(agentsList);

      const logsData = callLogRes.data;
      const logs: any[] = Array.isArray(logsData) ? logsData : Array.isArray(logsData?.data) ? logsData.data : [];
      if (logs.length > 0) {
        setMonitorUCID(logs[logs.length - 1]?.monitorUCID || null);
      }
    } catch (error: any) {
      const msg = error?.response?.data?.message || "Failed to merge call";
      ShowAlert("warning", msg);
    } finally {
      setLoading(false);
    }
  };

  const handleOpen = () => {
    setIsOpen(true);
    fetchAgentsAndCallId();
  };

  const handleClose = () => setIsOpen(false);

  const handleMerge = async (agentId: string) => {
    if (!monitorUCID) {
      ShowAlert("warning", "No active call found");
      return;
    }
    setMerging(agentId);
    try {
      await callMergeApi({ agent_id: agentId, call_id: monitorUCID });
      ShowAlert("success", "Call merged successfully");
      handleClose();
    } catch (error: any) {
      const msg = error?.response?.data?.message || "Failed to merge call";
      ShowAlert("error", msg);
    } finally {
      setMerging(null);
    }
  };

  return { isOpen, agents, loading, merging, handleOpen, handleClose, handleMerge };
};
